import React, { useState, useRef } from 'react';
import {
    Layout,
    Card,
    Input,
    Button,
    Space,
    Typography,
    Row,
    Col,
    Spin,
    message,
    Divider,
    Modal,
} from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { FormCanvas } from './components/FormCanvas';
import { FieldConfigDrawer } from './components/FieldConfigDrawer';
import { SectionManagerModal } from './components/SectionManagerModal';
import {
    SaveOutlined,
    EyeOutlined,
    ArrowLeftOutlined,
    PlusOutlined,
    DownloadOutlined,
    UploadOutlined,
    FolderOutlined,
} from '@ant-design/icons';
import { FormPreviewV2 } from './components/FormPreviewV2';
import { SqliteUploadSection } from './components/SqliteUploadSection';
import { useFormBuilder } from './hooks/useFormBuilder';
import { useSqliteUpload } from './hooks/useSqliteUpload';
import { FormField_V2, FieldType_V2 } from '../../common/typescript/models/CollectFormV2';
import { CollectFormTemplateAPIs } from '../../common/typescript/apis/CollectFormTemplateAPIs';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { UUIDUtils } from '../../common/typescript/utils/UuidUtils';
import Routes from '../../routes/routes';

const { Header, Content, Sider } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;

export const FormBuilderScreen: React.FC = () => {
    const navigate = useNavigate();
    const { templateId } = useParams<{ templateId?: string }>();

    const {
        formSchema,
        metadata,
        uploadedFiles,
        templateUuid,
        loading,
        addField,
        updateField,
        deleteField,
        reorderFields,
        createFieldFromType,
        saveTemplate,
        loadTemplate,
        resetForm,
        setMetadata,
        setUploadedFiles,
        setFormSchema,
        setLoading,
        addSection,
        updateSection,
        deleteSection,
    } = useFormBuilder();

    const { uploadSqlite } = useSqliteUpload(templateUuid);

    const [drawerVisible, setDrawerVisible] = useState(false);
    const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');
    const [selectedField, setSelectedField] = useState<FormField_V2 | null>(null);
    const [localSqliteFiles, setLocalSqliteFiles] = useState<File[]>([]);
    const [exportLoading, setExportLoading] = useState(false);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [sectionModalVisible, setSectionModalVisible] = useState(false);
    const importFileRef = useRef<HTMLInputElement>(null);

    // Load template if editing or reset if new
    React.useEffect(() => {
        if (templateId && templateId !== 'new') {
            loadTemplate(templateId);
        } else {
            resetForm();
        }
    }, [templateId, loadTemplate, resetForm]);

    const handleAddFieldClick = () => {
        // Open drawer without pre-selecting field type
        setSelectedField(null);
        setDrawerMode('create');
        setDrawerVisible(true);
    };

    const handleFieldEdit = (field: FormField_V2) => {
        setSelectedField(field);
        setDrawerMode('edit');
        setDrawerVisible(true);
    };

    const handleFieldSave = (field: FormField_V2) => {
        if (drawerMode === 'create') {
            addField(field);
            message.success(`Đã thêm trường "${field.label}"`);
        } else {
            updateField(field.id, field);
            message.success(`Đã cập nhật trường "${field.label}"`);
        }
        setDrawerVisible(false);
        setSelectedField(null);
    };

    const handleFieldDelete = (fieldId: string) => {
        deleteField(fieldId);
        message.success('Đã xóa trường');
    };

    const handleSave = async () => {
        const success = await saveTemplate();
        if (success && (!templateId || templateId === 'new') && templateUuid) {
            // Navigate to edit mode after first save
            navigate(`/collect-form-template/design/${templateUuid}`);
        }
    };

    const handleSqliteUpload = async (file: File) => {
        // Store file locally instead of uploading to server
        setLocalSqliteFiles([...localSqliteFiles, file]);
        setUploadedFiles([...uploadedFiles, file.name]);
        message.success(`Đã thêm cơ sở dữ liệu: ${file.name}`);
        return { objectName: file.name };
    };

    const handleSqliteDelete = (objectName: string) => {
        // Remove from both local files and uploaded files list
        setLocalSqliteFiles(localSqliteFiles.filter((f) => f.name !== objectName));
        setUploadedFiles(uploadedFiles.filter((f) => f !== objectName));
        message.success('Đã xóa cơ sở dữ liệu');
    };

    const handleExportOffline = async () => {
        try {
            // Validation
            if (!metadata.name.trim()) {
                message.error('Vui lòng nhập tên biểu mẫu');
                return;
            }
            if (formSchema.fields.length === 0) {
                message.error('Vui lòng thêm ít nhất một trường dữ liệu');
                return;
            }

            setExportLoading(true);

            const zip = new JSZip();
            const timestamp = Date.now();

            // 1. Add manifest.json
            const manifest = {
                version: '2.0.0',
                exportedAt: new Date().toISOString(),
                exportedBy: 'geocloud-web (client-side)',
                formName: metadata.name,
            };
            zip.file('manifest.json', JSON.stringify(manifest, null, 2));

            // 2. Add form.json
            const formJson = {
                uuid: UUIDUtils.generateUUID(),
                name: metadata.name,
                description: metadata.description,
                form_version: 2,
                content: formSchema,
            };
            zip.file('form.json', JSON.stringify(formJson, null, 2));

            // 3. Add SQLite files if present
            if (localSqliteFiles.length > 0) {
                const databasesFolder = zip.folder('databases');
                if (databasesFolder) {
                    localSqliteFiles.forEach((file) => {
                        databasesFolder.file(file.name, file);
                    });
                }
            }

            // Generate ZIP
            const content = await zip.generateAsync({ type: 'blob' });

            // Download
            const sanitizedName = metadata.name.replace(/[^a-z0-9]/gi, '-').toLowerCase();
            const filename = `form-${sanitizedName}-${timestamp}.zip`;
            saveAs(content, filename);

            message.success('Xuất biểu mẫu thành công!');
        } catch (err) {
            console.error('Export error:', err);
            message.error(`Xuất file thất bại: ${err instanceof Error ? err.message : 'Lỗi không xác định'}`);
        } finally {
            setExportLoading(false);
        }
    };

    const handlePreview = () => {
        setPreviewVisible(true);
    };

    const handleImportClick = () => {
        if (importFileRef.current) {
            importFileRef.current.click();
        }
    };

    const handleImportZip = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            setLoading(true);
            const zip = await JSZip.loadAsync(file);

            // 1. Read form.json
            const formJsonFile = zip.file('form.json');
            if (!formJsonFile) {
                throw new Error('File ZIP không đúng định dạng (thiếu form.json)');
            }
            const formJsonContent = await formJsonFile.async('string');
            const formData = JSON.parse(formJsonContent);

            // Update Metadata and Schema
            setMetadata({
                name: formData.name || '',
                description: formData.description || '',
            });

            let importedSchema = null;

            // Determine schema location
            if (formData.content && Array.isArray(formData.content.fields)) {
                importedSchema = formData.content;
            } else if (formData.schema && Array.isArray(formData.schema.fields)) {
                importedSchema = formData.schema;
            } else if (Array.isArray(formData.fields)) {
                importedSchema = formData;
            }

            if (importedSchema) {
                setFormSchema(importedSchema);
                message.success(`Đã nhập biểu mẫu: ${importedSchema.fields?.length || 0} trường, ${importedSchema.sections?.length || 0} phần.`);
            } else {
                message.warning('Không tìm thấy dữ liệu schema hợp lệ (fields) trong form.json');
                console.warn('Invalid JSON structure:', formData);
            }

            // 2. Read SQLite files
            const databasesFolder = zip.folder('databases');
            const newSqliteFiles: File[] = [];

            if (databasesFolder) {
                const dbFiles: Array<{ name: string; fileObj: any }> = [];
                databasesFolder.forEach((relativePath, fileObj) => {
                    if (!fileObj.dir) {
                        dbFiles.push({ name: relativePath, fileObj });
                    }
                });

                for (const db of dbFiles) {
                    const blob = await db.fileObj.async('blob');
                    const dbFile = new File([blob], db.name);
                    newSqliteFiles.push(dbFile);
                }
            }

            setLocalSqliteFiles(newSqliteFiles);
            setUploadedFiles(newSqliteFiles.map(f => f.name));

            message.success('Nhập biểu mẫu thành công!');
        } catch (err) {
            console.error('Import error:', err);
            message.error(`Nhập file thất bại: ${err instanceof Error ? err.message : 'Lỗi không xác định'}`);
        } finally {
            setLoading(false);
            // Reset input
            if (event.target) {
                event.target.value = '';
            }
        }
    };

    return (
        <Spin spinning={loading}>
            <Layout style={{ height: '100vh', overflow: 'hidden' }}>
                <input
                    type="file"
                    accept=".zip"
                    ref={importFileRef}
                    style={{ display: 'none' }}
                    onChange={handleImportZip}
                />

                {/* Header */}
                <Header
                    style={{
                        background: '#fff',
                        padding: '0 24px',
                        borderBottom: '1px solid #f0f0f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Space>
                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate(Routes.COLLECT_FORM_TEMPLATES)}
                        >
                            Quay lại
                        </Button>
                        <Title level={4} style={{ margin: 0 }}>
                            {templateId && templateId !== 'new' ? 'Chỉnh sửa biểu mẫu' : 'Thiết kế biểu mẫu'}
                        </Title>
                    </Space>

                    <Space>
                        <Button icon={<FolderOutlined />} onClick={() => setSectionModalVisible(true)}>
                            Quản lý phần
                        </Button>
                        <Button icon={<UploadOutlined />} onClick={handleImportClick}>
                            Nhập ZIP
                        </Button>
                        <Button icon={<EyeOutlined />} onClick={handlePreview}>
                            Xem trước
                        </Button>
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={handleExportOffline}
                            loading={exportLoading}
                        >
                            Xuất ZIP Offline
                        </Button>
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            onClick={handleSave}
                            loading={loading}
                        >
                            Lưu biểu mẫu
                        </Button>
                    </Space>
                </Header>

                <Layout>
                    {/* Main Content - Form Canvas */}
                    <Content style={{ padding: '24px', background: '#f5f5f5', overflowY: 'auto' }}>
                        <Space direction="vertical" style={{ width: '100%' }} size="large">
                            {/* Form Metadata */}
                            <Card>
                                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                    <div>
                                        <Text strong>Tên biểu mẫu *</Text>
                                        <Input
                                            size="large"
                                            placeholder="Nhập tên biểu mẫu"
                                            value={metadata.name}
                                            onChange={(e) => setMetadata({ ...metadata, name: e.target.value })}
                                            style={{ marginTop: 8 }}
                                        />
                                    </div>
                                    <div>
                                        <Text strong>Mô tả</Text>
                                        <TextArea
                                            rows={3}
                                            placeholder="Nhập mô tả biểu mẫu"
                                            value={metadata.description}
                                            onChange={(e) =>
                                                setMetadata({ ...metadata, description: e.target.value })
                                            }
                                            style={{ marginTop: 8 }}
                                        />
                                    </div>
                                </Space>
                            </Card>

                            {/* Form Fields */}
                            <Card
                                title={
                                    <Space>
                                        <span>Các trường dữ liệu ({formSchema.fields.length})</span>
                                    </Space>
                                }
                                extra={
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={handleAddFieldClick}
                                    >
                                        Thêm trường
                                    </Button>
                                }
                            >
                                <FormCanvas
                                    fields={formSchema.fields}
                                    onFieldEdit={handleFieldEdit}
                                    onFieldDelete={handleFieldDelete}
                                    onFieldReorder={reorderFields}
                                />
                            </Card>
                        </Space>
                    </Content>

                    {/* Right Sidebar - SQLite Upload */}
                    <Sider width={300} style={{ background: '#fff', borderLeft: '1px solid #f0f0f0', overflowY: 'auto' }}>
                        <SqliteUploadSection
                            templateUuid={templateUuid}
                            uploadedFiles={uploadedFiles}
                            onUploadSuccess={(objectName) => { }}
                            onDeleteFile={handleSqliteDelete}
                            onUpload={handleSqliteUpload}
                        />
                    </Sider>
                </Layout>
            </Layout>

            {/* Field Configuration Drawer */}
            <FieldConfigDrawer
                visible={drawerVisible}
                field={selectedField}
                mode={drawerMode}
                onClose={() => {
                    setDrawerVisible(false);
                    setSelectedField(null);
                }}
                onSave={handleFieldSave}
                existingFieldIds={formSchema.fields.map((f) => f.id)}
                uploadedDatabases={uploadedFiles}
                sqliteFiles={localSqliteFiles}
                sections={formSchema.sections || []}
            />

            <Modal
                title="Xem trước biểu mẫu"
                open={previewVisible}
                onCancel={() => setPreviewVisible(false)}
                footer={null}
                width={800}
            >
                <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
                    <FormPreviewV2
                        schema={formSchema}
                        sqliteFiles={localSqliteFiles}
                    /></div>
            </Modal>
            <SectionManagerModal
                visible={sectionModalVisible}
                sections={formSchema.sections || []}
                onClose={() => setSectionModalVisible(false)}
                onAdd={addSection}
                onUpdate={updateSection}
                onDelete={deleteSection}
            />
        </Spin>
    );
};
